import type { RestoreOnCreateConflictConfig } from "../useRestoreOnCreateConflict";

type PayloadSpreadRestoreConfigParams<
    TConflict extends { existingId: string },
    TPending extends object,
    TRestorePayload extends { id: string },
    TDetail extends { id: string },
    TError extends { conflict: TConflict },
> = {
    ConflictErrorClass: abstract new (...args: never[]) => TError;
    isRestoreAvailable: (conflict: unknown, expectedCode: string) => conflict is TConflict;
    deletedRestoreAvailableCode: string;
    modalTitle: string;
    modalBody: (conflict: TConflict) => string;
    successLabel: (conflict: TConflict) => string;
    restoreDeletedWithCreateValues: (payload: TRestorePayload) => Promise<TDetail>;
    onRestored: (detail: TDetail, conflict: TConflict) => void;
};

export function createPayloadSpreadRestoreConfig<
    TConflict extends { existingId: string },
    TPending extends object,
    TRestorePayload extends { id: string },
    TDetail extends { id: string },
    TError extends { conflict: TConflict },
>(
    params: PayloadSpreadRestoreConfigParams<TConflict, TPending, TRestorePayload, TDetail, TError>
): RestoreOnCreateConflictConfig<TConflict, TPending, TRestorePayload, TDetail> {
    const {
        ConflictErrorClass,
        isRestoreAvailable,
        deletedRestoreAvailableCode,
        modalTitle,
        modalBody,
        successLabel,
        restoreDeletedWithCreateValues,
        onRestored,
    } = params;

    return {
        isConflictError: (err: unknown): err is TError =>
            err instanceof ConflictErrorClass,
        isRestoreAvailable: (conflict: unknown): conflict is TConflict =>
            isRestoreAvailable(conflict, deletedRestoreAvailableCode),
        modal: {
            title: modalTitle,
            body: modalBody,
        },
        buildRestorePayload: (conflict, pending) =>
            ({ id: conflict.existingId, ...pending }) as TRestorePayload,
        formatSuccessMessage: (conflict) => `"${successLabel(conflict)}" restored successfully!`,
        restoreDeletedWithCreateValues,
        onRestored,
    };
}
